import FormAutocompleteField from '../../../components/form/FormAutocompleteField';
import FormMapField from '../../../components/form/FormMapField';
import type { GeoLocation } from '../../../providers/LocationProvider';
import {
  __,
  composeValidators,
  required,
  validateDistance,
} from '../../../utils';
import { Box } from '@presov.sk/presov-ui-react';
import React from 'react';
import { Form, FormSpy } from 'react-final-form';

export type LocationFilterValue = {
  location?: GeoLocation;
  distance?: string;
};

type Props = {
  value: LocationFilterValue;
  onChange: (value: LocationFilterValue) => void;
};

/** `1 km` aj `1000` znamenajú to isté - API berie metre. */
const normalizeDistance = (value: string) => {
  const match = value.trim().match(/^(\d+)\s*(km|m)?$/i);

  if (!match) {
    return value;
  }

  const [, number, unit] = match;

  return unit?.toLowerCase() === 'km'
    ? (parseInt(number, 10) * 1000).toString()
    : parseInt(number, 10).toString();
};

const DISTANCE_OPTIONS = [
  { label: '70 m', value: '70' },
  { label: '200 m', value: '200' },
  { label: '500 m', value: '500' },
  { label: '1 km', value: '1000' },
];

/**
 * Lokalita a okolie ako obsah `FilterPanel`-u.
 *
 * Schéma `FilterField` pozná select, toggle, text, number a date - mapa medzi
 * ne nepatrí a dvojica „bod + polomer" tiež nie: polomer bez bodu nemá čo
 * znamenať, takže to nie sú dva nezávislé filtre. Preto ide dovnútra panela
 * ako `children`, nie do druhého drawer-a vedľa neho.
 *
 * `FormMapField` je adaptér react-final-form (stojí na `useForm`
 * a `useFormState`), takže potrebuje vlastný `Form`. Hodnoty putujú nahor
 * z `FormSpy`, teda z callbacku predplatného - nie počas renderu.
 */
const ReportsLocationFilter: React.FC<Props> = ({ value, onChange }) => {
  // `FormSpy` ohlási aj počiatočný stav a rodič hodnoty píše do URL, z ktorej
  // sa vzápätí vrátia sem ako `initialValues`. Bez tejto pamäte by sa tie dve
  // strany prehadzovali dokola; porovnáva sa to, čo naozaj odišlo nahor.
  const pushed = React.useRef(JSON.stringify(value));

  return (
    <Form<LocationFilterValue>
      onSubmit={() => undefined}
      initialValues={value}
      render={({ values, form }) => (
        <>
          <FormSpy<LocationFilterValue>
            subscription={{ values: true }}
            onChange={({ values: next }) => {
              // Polomer bez bodu je neúplný filter - nemá sa čo posielať.
              if (next.location && !next.distance) {
                form.change('distance', '1000');
                return;
              }

              const payload: LocationFilterValue = {
                location: next.location,
                distance: next.location ? next.distance : undefined,
              };
              const serialized = JSON.stringify(payload);

              if (serialized === pushed.current) {
                return;
              }

              pushed.current = serialized;
              onChange(payload);
            }}
          />

          <Box sx={{ '& > div': { marginBottom: 0 } }}>
            <FormMapField
              name='location'
              label={__('Lokalita')}
              searchInitialPosition={Boolean(value.location)}
              initialPosition={value.location}
              deleteWithoutWarningLabel
              withoutDeviceLocation
            />
          </Box>

          {values.location ? (
            <FormAutocompleteField
              freeSolo
              name='distance'
              label={__('Vzdialenosť')}
              options={DISTANCE_OPTIONS}
              validate={composeValidators(required, validateDistance)}
              onInputChange={(_, newValue) => {
                if (newValue !== null && newValue !== '') {
                  form.change('distance', normalizeDistance(newValue));
                }
              }}
            />
          ) : null}
        </>
      )}
    />
  );
};

export default ReportsLocationFilter;
